import { useCallback, useEffect, useMemo, useState } from 'react';
import { replacementSavedMethodId } from '@/lib/payments/savedPaymentMethods';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { DEFAULT_PAYMENT_LIST_FILTERS } from '@/components/organisms/payments/payment-list-filters';
import { AlertTriangle, ClipboardList, History, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { EntityIndexHeader } from '@/components/layouts';
import { DataTableFilterSection, type FinancialDataTableSortDir } from '@/components/organisms';
import type { PlanActions } from '@/components/organisms/recurring-billing/RecurringBillingActions';
import { RecurringBillingDetailSheet } from '@/components/organisms/recurring-billing/RecurringBillingDetailSheet';
import {
  ChangePaymentMethodDialog,
  RepairLinkDialog,
} from '@/components/organisms/recurring-billing/RecurringBillingDialogs';
import {
  RecurringBillingMobilePlanList,
  RecurringBillingMobileRunList,
} from '@/components/organisms/recurring-billing/RecurringBillingMobileLists';
import {
  RecurringBillingPlanTable,
  RecurringBillingRunTable,
} from '@/components/organisms/recurring-billing/RecurringBillingTables';
import { DataTableSearchFilterField } from '@/components/molecules';
import { useContactPaymentMethods } from '@/hooks/contacts/useContactPaymentMethods';
import {
  useCreateRecurringBillingRepairLink,
  useDispatchRecurringBillingNow,
  usePauseRecurringBillingPlan,
  useRecurringBillingExceptions,
  useRecurringBillingPlans,
  useRecurringBillingRuns,
  useRetryRecurringBillingPlanFailure,
  useResumeRecurringBillingPlan,
  useRunRecurringBillingPlanNow,
  useUpdateRecurringPlanPaymentMethod,
} from '@/hooks/recurring-billing/useRecurringBilling';
import {
  buildRecurringBillingSearch,
  compareValues,
  contactLabel,
  countByRunStatus,
  countByStatus,
  normalizeOptionalSearchId,
  normalizeTab,
  planLabel,
  planNextRunAt,
  planSearchText,
  runSearchText,
  storedScheduleLabel,
  type PlanColumnKey,
  type RecurringBillingTab,
  type RunColumnKey,
  type SelectedRecord,
} from '@/lib/recurring-billing/viewModel';
import { cn } from '@/lib/utils';
import type {
  RecurringBillingPlanStatus,
  RecurringBillingPlanSummary,
  RecurringBillingRepairLinkResponse,
  RecurringBillingRunStatus,
  RecurringBillingRunSummary,
} from '@/types/recurring-billing';
import type { RecurringStatus } from '@/types/job';
import type { ContactSectionId } from '@/components/organisms/contacts/detail/ContactSectionNav';

export const Route = createFileRoute('/_dashboard/dashboard/recurring-billing')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: normalizeTab(search.tab),
    jobId: normalizeOptionalSearchId(search.jobId),
    contactId: normalizeOptionalSearchId(search.contactId),
    planId: normalizeOptionalSearchId(search.planId),
  }),
  component: RecurringBillingPage,
});

type PlanStatusFilter =
  | 'ALL'
  | RecurringBillingPlanStatus
  | Extract<RecurringStatus, 'PAST_DUE' | 'SUSPENDED'>;

function isRecoveryStatusFilter(
  value: PlanStatusFilter,
): value is Extract<RecurringStatus, 'PAST_DUE' | 'SUSPENDED'> {
  return value === 'PAST_DUE' || value === 'SUSPENDED';
}

function isPlanStatusFilter(value: PlanStatusFilter): value is RecurringBillingPlanStatus {
  return value !== 'ALL' && !isRecoveryStatusFilter(value);
}

function planMatchesStatusFilter(plan: RecurringBillingPlanSummary, filter: PlanStatusFilter) {
  if (filter === 'ALL') return true;
  if (isRecoveryStatusFilter(filter)) return plan.job.recurringStatus === filter;
  return plan.status === filter;
}

function RecurringBillingPage() {
  const router = useRouter();
  const search = Route.useSearch();

  const [activeTab, setActiveTab] = useState<RecurringBillingTab>(search.tab);
  const [tableSearch, setTableSearch] = useState('');
  const [planStatus, setPlanStatus] = useState<PlanStatusFilter>('ALL');
  const [runStatus, setRunStatus] = useState<'ALL' | RecurringBillingRunStatus>('ALL');
  const [planSortKey, setPlanSortKey] = useState<PlanColumnKey | null>('nextRun');
  const [planSortDir, setPlanSortDir] = useState<FinancialDataTableSortDir>('asc');
  const [runSortKey, setRunSortKey] = useState<RunColumnKey | null>('scheduled');
  const [runSortDir, setRunSortDir] = useState<FinancialDataTableSortDir>('desc');
  const [selectedRecord, setSelectedRecord] = useState<SelectedRecord>(null);
  const [paymentMethodPlan, setPaymentMethodPlan] = useState<RecurringBillingPlanSummary | null>(
    null,
  );
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [repairLinkResponse, setRepairLinkResponse] =
    useState<RecurringBillingRepairLinkResponse | null>(null);

  useEffect(() => {
    if (search.tab !== activeTab) {
      setActiveTab(search.tab);
    }
  }, [activeTab, search.tab]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const hasNullishIdParam = ['jobId', 'contactId', 'planId'].some((key) => {
      const value = params.get(key);
      return value === '' || value === 'null';
    });
    const hasPlanIdOutsideRuns = search.tab !== 'runs' && params.has('planId');
    if (!hasNullishIdParam && !hasPlanIdOutsideRuns) return;

    void router.navigate({
      to: '/dashboard/recurring-billing',
      search: buildRecurringBillingSearch({
        tab: search.tab,
        jobId: search.jobId,
        contactId: search.contactId,
        planId: search.planId,
      }),
      replace: true,
    });
  }, [router, search.contactId, search.jobId, search.planId, search.tab]);

  const basePlanParams = useMemo(
    () => ({
      page: 1,
      limit: 50,
      jobId: search.jobId ?? undefined,
      contactId: search.contactId ?? undefined,
    }),
    [search.contactId, search.jobId],
  );
  const metricPlansQuery = useRecurringBillingPlans(basePlanParams);
  const plansQuery = useRecurringBillingPlans({
    ...basePlanParams,
    status: isPlanStatusFilter(planStatus) ? planStatus : undefined,
    recurringStatus: isRecoveryStatusFilter(planStatus) ? planStatus : undefined,
  });
  // When the last dispatch answered: the runs it queued bill a moment later.
  const [dispatchedAt, setDispatchedAt] = useState<number | null>(null);
  const runsQuery = useRecurringBillingRuns(
    {
      page: 1,
      limit: 50,
      planId: search.planId ?? undefined,
      jobId: search.jobId ?? undefined,
      contactId: search.contactId ?? undefined,
    },
    { dispatchedAt },
  );
  const exceptionsQuery = useRecurringBillingExceptions({
    page: 1,
    limit: 50,
    jobId: search.jobId ?? undefined,
    contactId: search.contactId ?? undefined,
  });

  const pausePlan = usePauseRecurringBillingPlan();
  const resumePlan = useResumeRecurringBillingPlan();
  const runPlanNow = useRunRecurringBillingPlanNow();
  const retryPlanFailure = useRetryRecurringBillingPlanFailure();
  const updatePlanPaymentMethod = useUpdateRecurringPlanPaymentMethod();
  const dispatchDuePlans = useDispatchRecurringBillingNow();
  const createRepairLink = useCreateRecurringBillingRepairLink();

  const methodsQuery = useContactPaymentMethods(paymentMethodPlan?.contactId ?? '');

  useEffect(() => {
    if (!paymentMethodPlan) {
      setSelectedPaymentMethodId('');
      return;
    }

    const available = methodsQuery.data ?? [];
    setSelectedPaymentMethodId(
      replacementSavedMethodId(available, paymentMethodPlan.paymentMethodId),
    );
  }, [paymentMethodPlan, methodsQuery.data]);

  const allPlans = useMemo(() => metricPlansQuery.data?.data ?? [], [metricPlansQuery.data?.data]);
  const tablePlans = useMemo(() => plansQuery.data?.data ?? [], [plansQuery.data?.data]);
  const allRuns = useMemo(() => runsQuery.data?.data ?? [], [runsQuery.data?.data]);

  const filteredPlans = useMemo(() => {
    const query = tableSearch.trim().toLowerCase();
    const rows = tablePlans.filter(
      (plan) =>
        planMatchesStatusFilter(plan, planStatus) &&
        (!query || planSearchText(plan).includes(query)),
    );
    if (!planSortKey) return rows;
    return [...rows].sort((a, b) => {
      const dir = planSortDir === 'asc' ? 1 : -1;
      if (planSortKey === 'job') {
        return compareValues(planLabel(a), planLabel(b)) * dir;
      }
      if (planSortKey === 'contact') {
        return compareValues(contactLabel(a), contactLabel(b)) * dir;
      }
      if (planSortKey === 'billing') {
        return compareValues(storedScheduleLabel(a), storedScheduleLabel(b)) * dir;
      }
      if (planSortKey === 'status') return compareValues(a.status, b.status) * dir;
      if (planSortKey === 'nextRun') {
        return compareValues(planNextRunAt(a), planNextRunAt(b)) * dir;
      }
      return (
        compareValues(
          a.lastFailureAt ?? a.lastSuccessAt ?? a.lastRunAt,
          b.lastFailureAt ?? b.lastSuccessAt ?? b.lastRunAt,
        ) * dir
      );
    });
  }, [planSortDir, planSortKey, planStatus, tablePlans, tableSearch]);

  const filteredRuns = useMemo(() => {
    const query = tableSearch.trim().toLowerCase();
    const rows = allRuns.filter(
      (run) =>
        (runStatus === 'ALL' || run.status === runStatus) &&
        (!query || runSearchText(run).includes(query)),
    );
    if (!runSortKey) return rows;
    return [...rows].sort((a, b) => {
      const dir = runSortDir === 'asc' ? 1 : -1;
      if (runSortKey === 'scheduled') {
        return compareValues(a.scheduledFor, b.scheduledFor) * dir;
      }
      if (runSortKey === 'job') {
        return compareValues(a.job.jobNumber ?? a.job.name, b.job.jobNumber ?? b.job.name) * dir;
      }
      if (runSortKey === 'contact') {
        return (
          compareValues(
            contactLabel({ contact: a.contact }),
            contactLabel({ contact: b.contact }),
          ) * dir
        );
      }
      if (runSortKey === 'status') {
        return compareValues(a.status, b.status) * dir;
      }
      return (
        compareValues(
          a.finishedAt ?? a.startedAt ?? a.createdAt,
          b.finishedAt ?? b.startedAt ?? b.createdAt,
        ) * dir
      );
    });
  }, [allRuns, runSortDir, runSortKey, runStatus, tableSearch]);

  const filteredExceptions = useMemo(() => {
    const query = tableSearch.trim().toLowerCase();
    const rows = (exceptionsQuery.data?.data ?? []).filter(
      (plan) => !query || planSearchText(plan).includes(query),
    );
    if (!planSortKey) return rows;
    return [...rows].sort((a, b) => {
      const dir = planSortDir === 'asc' ? 1 : -1;
      if (planSortKey === 'job') return compareValues(planLabel(a), planLabel(b)) * dir;
      if (planSortKey === 'contact') return compareValues(contactLabel(a), contactLabel(b)) * dir;
      if (planSortKey === 'billing') {
        return compareValues(storedScheduleLabel(a), storedScheduleLabel(b)) * dir;
      }
      if (planSortKey === 'status') return compareValues(a.status, b.status) * dir;
      if (planSortKey === 'nextRun') return compareValues(planNextRunAt(a), planNextRunAt(b)) * dir;
      return (
        compareValues(
          a.lastFailureAt ?? a.lastSuccessAt ?? a.lastRunAt,
          b.lastFailureAt ?? b.lastSuccessAt ?? b.lastRunAt,
        ) * dir
      );
    });
  }, [exceptionsQuery.data?.data, planSortDir, planSortKey, tableSearch]);

  function updateTab(tab: RecurringBillingTab) {
    setActiveTab(tab);
    void router.navigate({
      to: '/dashboard/recurring-billing',
      search: buildRecurringBillingSearch({
        tab,
        jobId: search.jobId,
        contactId: search.contactId,
        planId: search.planId,
      }),
      replace: true,
    });
  }

  function updateRouteFilter(key: 'jobId' | 'contactId' | 'planId', value: string) {
    const nextValue = normalizeOptionalSearchId(value);
    void router.navigate({
      to: '/dashboard/recurring-billing',
      search: buildRecurringBillingSearch({
        tab: activeTab,
        jobId: key === 'jobId' ? nextValue : search.jobId,
        contactId: key === 'contactId' ? nextValue : search.contactId,
        planId: key === 'planId' ? nextValue : search.planId,
      }),
      replace: true,
    });
  }

  function clearRouteFilters() {
    void router.navigate({
      to: '/dashboard/recurring-billing',
      search: buildRecurringBillingSearch({ tab: activeTab }),
      replace: true,
    });
  }

  function clearAllTableFilters() {
    setTableSearch('');
    setPlanStatus('ALL');
    setRunStatus('ALL');
    clearRouteFilters();
  }

  function handlePlanSort(key: PlanColumnKey) {
    setPlanSortKey((current) => {
      if (current !== key) {
        setPlanSortDir('asc');
        return key;
      }
      setPlanSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return current;
    });
  }

  function handleRunSort(key: RunColumnKey) {
    setRunSortKey((current) => {
      if (current !== key) {
        setRunSortDir('asc');
        return key;
      }
      setRunSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return current;
    });
  }

  const openJob = useCallback(
    (plan: Pick<RecurringBillingPlanSummary, 'jobId'>) => {
      void router.navigate({ to: `/dashboard/jobs/${plan.jobId}` as string });
    },
    [router],
  );

  const openJobBilling = useCallback(
    (plan: Pick<RecurringBillingPlanSummary, 'jobId'>) => {
      void router.navigate({ to: `/dashboard/jobs/${plan.jobId}/edit` as string });
    },
    [router],
  );

  const openContactPaymentMethods = useCallback(
    (plan: Pick<RecurringBillingPlanSummary, 'contactId'>) => {
      if (!plan.contactId) return;
      void router.navigate({
        to: '/dashboard/contacts/$contactId',
        params: { contactId: plan.contactId },
        // Saved cards live under the contact's Payments section.
        search: { tab: 'payments' satisfies ContactSectionId } as never,
      });
    },
    [router],
  );

  const openRunInvoice = useCallback(
    (run: Pick<RecurringBillingRunSummary, 'invoiceId'>) => {
      if (!run.invoiceId) return;
      void router.navigate({ to: `/dashboard/invoices/${run.invoiceId}` as string });
    },
    [router],
  );

  const openRunPayment = useCallback(
    (run: Pick<RecurringBillingRunSummary, 'paymentId'>) => {
      if (!run.paymentId) return;
      void router.navigate({
        to: '/dashboard/payments/$id',
        params: { id: run.paymentId },
        search: DEFAULT_PAYMENT_LIST_FILTERS,
      });
    },
    [router],
  );

  const sendRepairLink = useCallback(
    async (plan: RecurringBillingPlanSummary) => {
      try {
        const response = await createRepairLink.mutateAsync({ id: plan.id });
        setRepairLinkResponse(response);
      } catch {
        // The refusal is toasted by the mutation.
      }
    },
    [createRepairLink],
  );

  const actions = useMemo<PlanActions>(
    () => ({
      onPause: (plan) => pausePlan.mutate({ id: plan.id }),
      onResume: (plan) => resumePlan.mutate({ id: plan.id }),
      onRunNow: (plan) => runPlanNow.mutate({ id: plan.id }),
      onRetryFailure: (plan) => retryPlanFailure.mutate({ id: plan.id }),
      onChangeMethod: setPaymentMethodPlan,
      onSendRepairLink: sendRepairLink,
      onOpenContactPaymentMethods: openContactPaymentMethods,
      onOpenJob: openJob,
      onOpenJobBilling: openJobBilling,
    }),
    [
      openContactPaymentMethods,
      openJob,
      openJobBilling,
      pausePlan,
      resumePlan,
      retryPlanFailure,
      runPlanNow,
      sendRepairLink,
    ],
  );

  const toolbarActions = (
    <div className="flex shrink-0 items-center gap-2">
      <Tabs value={activeTab} onValueChange={(value) => updateTab(value as RecurringBillingTab)}>
        <TabsList className="h-8">
          <TabsTrigger value="plans" className="gap-1.5 px-2.5 text-xs after:hidden">
            <ClipboardList className="size-3.5" />
            Plans
          </TabsTrigger>
          <TabsTrigger value="runs" className="gap-1.5 px-2.5 text-xs after:hidden">
            <History className="size-3.5" />
            Runs
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="gap-1.5 px-2.5 text-xs after:hidden">
            <AlertTriangle className="size-3.5" />
            Exceptions
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="h-7 w-36 shrink-0">
        {activeTab === 'plans' ? (
          <PlanStatusSelect value={planStatus} onChange={setPlanStatus} />
        ) : null}
        {activeTab === 'runs' ? (
          <RunStatusSelect value={runStatus} onChange={setRunStatus} />
        ) : null}
      </div>
    </div>
  );

  const mobileToolbar = (
    <div className="md:hidden space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={tableSearch}
            onChange={(event) => setTableSearch(event.target.value)}
            placeholder="Search job or contact"
            className="h-10 pl-9"
          />
        </div>
        <div className="w-36 shrink-0">
          {activeTab === 'plans' ? (
            <PlanStatusSelect value={planStatus} onChange={setPlanStatus} />
          ) : null}
          {activeTab === 'runs' ? (
            <RunStatusSelect value={runStatus} onChange={setRunStatus} />
          ) : null}
          {activeTab === 'exceptions' ? (
            <Button variant="outline" className="h-10 w-full justify-center" disabled>
              All
            </Button>
          ) : null}
        </div>
      </div>
      <Tabs value={activeTab} onValueChange={(value) => updateTab(value as RecurringBillingTab)}>
        <TabsList className="h-10 w-full justify-start overflow-x-auto">
          <TabsTrigger value="plans" className="gap-1.5 px-3 text-sm after:hidden">
            <ClipboardList className="size-4" />
            Plans
          </TabsTrigger>
          <TabsTrigger value="runs" className="gap-1.5 px-3 text-sm after:hidden">
            <History className="size-4" />
            Runs
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="gap-1.5 px-3 text-sm after:hidden">
            <AlertTriangle className="size-4" />
            Exceptions
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );

  const externalFilterCount =
    (search.jobId ? 1 : 0) +
    (search.contactId ? 1 : 0) +
    (activeTab === 'runs' && search.planId ? 1 : 0);
  const statusFilterCount =
    (activeTab === 'plans' && planStatus !== 'ALL') || (activeTab === 'runs' && runStatus !== 'ALL')
      ? 1
      : 0;
  const activeFilterCount = externalFilterCount + statusFilterCount;

  const externalFilterSections = (
    <>
      <DataTableFilterSection
        label="Job ID"
        active={Boolean(search.jobId)}
        onClear={() => updateRouteFilter('jobId', '')}
      >
        <DataTableSearchFilterField
          value={search.jobId ?? ''}
          onValueChange={(value) => updateRouteFilter('jobId', value)}
          placeholder="Filter by job UUID..."
        />
      </DataTableFilterSection>
      <DataTableFilterSection
        label="Contact ID"
        active={Boolean(search.contactId)}
        onClear={() => updateRouteFilter('contactId', '')}
      >
        <DataTableSearchFilterField
          value={search.contactId ?? ''}
          onValueChange={(value) => updateRouteFilter('contactId', value)}
          placeholder="Filter by contact UUID..."
        />
      </DataTableFilterSection>
      {activeTab === 'runs' ? (
        <DataTableFilterSection
          label="Plan ID"
          active={Boolean(search.planId)}
          onClear={() => updateRouteFilter('planId', '')}
        >
          <DataTableSearchFilterField
            value={search.planId ?? ''}
            onValueChange={(value) => updateRouteFilter('planId', value)}
            placeholder="Filter by plan UUID..."
          />
        </DataTableFilterSection>
      ) : null}
    </>
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <EntityIndexHeader
        title="Recurring Billing"
        actions={
          <Button
            size="icon"
            title="Dispatch due recurring billing"
            aria-label="Dispatch due recurring billing"
            onClick={() =>
              dispatchDuePlans.mutate(undefined, { onSuccess: () => setDispatchedAt(Date.now()) })
            }
            disabled={dispatchDuePlans.isPending}
          >
            <RefreshCw className={cn('size-4', dispatchDuePlans.isPending && 'animate-spin')} />
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricTile
          title="Active"
          value={countByStatus(allPlans, 'ACTIVE')}
          helper="Ready to bill"
          active={activeTab === 'plans' && planStatus === 'ACTIVE'}
          onClick={() => {
            setPlanStatus('ACTIVE');
            updateTab('plans');
          }}
        />
        <MetricTile
          title="Paused"
          value={countByStatus(allPlans, 'PAUSED')}
          helper="Operator hold"
          active={activeTab === 'plans' && planStatus === 'PAUSED'}
          onClick={() => {
            setPlanStatus('PAUSED');
            updateTab('plans');
          }}
        />
        <MetricTile
          title="Exceptions"
          value={exceptionsQuery.data?.total ?? 0}
          helper="Needs repair"
          active={activeTab === 'exceptions'}
          onClick={() => updateTab('exceptions')}
        />
        <MetricTile
          title="Failed Runs"
          value={countByRunStatus(allRuns, 'FAILED')}
          helper="Latest 50 runs"
          active={activeTab === 'runs' && runStatus === 'FAILED'}
          onClick={() => {
            setRunStatus('FAILED');
            updateTab('runs');
          }}
        />
      </div>

      {mobileToolbar}

      {activeTab === 'plans' ? (
        <>
          <RecurringBillingMobilePlanList
            plans={filteredPlans}
            isLoading={plansQuery.isLoading}
            isError={plansQuery.isError}
            emptyTitle="No recurring billing plans matched these filters."
            onRetry={() => void plansQuery.refetch()}
            onSelect={(plan) => setSelectedRecord({ type: 'plan', item: plan })}
            actions={actions}
            singular="plan"
          />
          <div className="hidden min-h-0 flex-1 md:block">
            <RecurringBillingPlanTable
              plans={filteredPlans}
              isLoading={plansQuery.isLoading}
              isError={plansQuery.isError}
              errorTitle="Failed to load recurring billing plans"
              onRetry={() => void plansQuery.refetch()}
              emptyIcon={ClipboardList}
              emptyTitle="No recurring billing plans matched these filters."
              storageKey="recurring-billing-plans-v2"
              singular="plan"
              onSelect={(plan) => setSelectedRecord({ type: 'plan', item: plan })}
              actions={actions}
              searchValue={tableSearch}
              onSearchChange={setTableSearch}
              showClearAll={Boolean(tableSearch.trim()) || activeFilterCount > 0}
              onClearAll={clearAllTableFilters}
              activeFilterCount={activeFilterCount}
              filterContent={externalFilterSections}
              filterResetDisabled={!tableSearch.trim() && activeFilterCount === 0}
              toolbarActions={toolbarActions}
              sortKey={planSortKey}
              sortDir={planSortDir}
              onSort={handlePlanSort}
            />
          </div>
        </>
      ) : null}

      {activeTab === 'runs' ? (
        <>
          <RecurringBillingMobileRunList
            runs={filteredRuns}
            isLoading={runsQuery.isLoading}
            isError={runsQuery.isError}
            emptyTitle="No recurring billing runs matched these filters."
            onRetry={() => void runsQuery.refetch()}
            onSelect={(run) => setSelectedRecord({ type: 'run', item: run })}
            onOpenInvoice={openRunInvoice}
            onOpenPayment={openRunPayment}
            onOpenJob={(item) =>
              void router.navigate({ to: `/dashboard/jobs/${item.jobId}` as string })
            }
          />
          <div className="hidden min-h-0 flex-1 md:block">
            <RecurringBillingRunTable
              runs={filteredRuns}
              isLoading={runsQuery.isLoading}
              isError={runsQuery.isError}
              onRetry={() => void runsQuery.refetch()}
              emptyIcon={History}
              onSelect={(run) => setSelectedRecord({ type: 'run', item: run })}
              onOpenInvoice={openRunInvoice}
              onOpenPayment={openRunPayment}
              onOpenJob={(item) =>
                void router.navigate({ to: `/dashboard/jobs/${item.jobId}` as string })
              }
              searchValue={tableSearch}
              onSearchChange={setTableSearch}
              showClearAll={Boolean(tableSearch.trim()) || activeFilterCount > 0}
              onClearAll={clearAllTableFilters}
              activeFilterCount={activeFilterCount}
              filterContent={externalFilterSections}
              filterResetDisabled={!tableSearch.trim() && activeFilterCount === 0}
              toolbarActions={toolbarActions}
              sortKey={runSortKey}
              sortDir={runSortDir}
              onSort={handleRunSort}
            />
          </div>
        </>
      ) : null}

      {activeTab === 'exceptions' ? (
        <>
          <RecurringBillingMobilePlanList
            plans={filteredExceptions}
            isLoading={exceptionsQuery.isLoading}
            isError={exceptionsQuery.isError}
            emptyTitle="No recurring billing exceptions are open."
            onRetry={() => void exceptionsQuery.refetch()}
            onSelect={(plan) => setSelectedRecord({ type: 'plan', item: plan })}
            actions={actions}
            singular="exception"
          />
          <div className="hidden min-h-0 flex-1 md:block">
            <RecurringBillingPlanTable
              plans={filteredExceptions}
              isLoading={exceptionsQuery.isLoading}
              isError={exceptionsQuery.isError}
              errorTitle="Failed to load recurring billing exceptions"
              onRetry={() => void exceptionsQuery.refetch()}
              emptyIcon={AlertTriangle}
              emptyTitle="No recurring billing exceptions are open."
              storageKey="recurring-billing-exceptions-v2"
              singular="exception"
              onSelect={(plan) => setSelectedRecord({ type: 'plan', item: plan })}
              actions={actions}
              searchValue={tableSearch}
              onSearchChange={setTableSearch}
              showClearAll={Boolean(tableSearch.trim()) || activeFilterCount > 0}
              onClearAll={clearAllTableFilters}
              activeFilterCount={activeFilterCount}
              filterContent={externalFilterSections}
              filterResetDisabled={!tableSearch.trim() && activeFilterCount === 0}
              toolbarActions={toolbarActions}
              sortKey={planSortKey}
              sortDir={planSortDir}
              onSort={handlePlanSort}
            />
          </div>
        </>
      ) : null}

      <RecurringBillingDetailSheet
        selected={selectedRecord}
        actions={actions}
        onOpenChange={(open) => !open && setSelectedRecord(null)}
        onOpenInvoice={openRunInvoice}
        onOpenPayment={openRunPayment}
        onOpenJob={(run) => void router.navigate({ to: `/dashboard/jobs/${run.jobId}` as string })}
      />

      <ChangePaymentMethodDialog
        plan={paymentMethodPlan}
        methods={methodsQuery.data ?? []}
        isLoading={methodsQuery.isLoading}
        selectedPaymentMethodId={selectedPaymentMethodId}
        onSelectedPaymentMethodIdChange={setSelectedPaymentMethodId}
        onOpenContactPaymentMethods={openContactPaymentMethods}
        onClose={() => setPaymentMethodPlan(null)}
        onSave={async () => {
          if (!paymentMethodPlan || !selectedPaymentMethodId) return;
          await updatePlanPaymentMethod.mutateAsync({
            id: paymentMethodPlan.id,
            paymentMethodId: selectedPaymentMethodId,
          });
          setPaymentMethodPlan(null);
        }}
        isSaving={updatePlanPaymentMethod.isPending}
      />

      <RepairLinkDialog response={repairLinkResponse} onClose={() => setRepairLinkResponse(null)} />
    </div>
  );
}
function MetricTile({
  title,
  value,
  helper,
  active,
  onClick,
}: {
  title: string;
  value: number;
  helper: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border bg-card px-4 py-3 text-left shadow-xs transition hover:border-foreground/25 hover:bg-muted/30',
        active && 'border-foreground bg-muted/40',
      )}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{helper}</div>
    </button>
  );
}

function PlanStatusSelect({
  value,
  onChange,
}: {
  value: PlanStatusFilter;
  onChange: (value: PlanStatusFilter) => void;
}) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as PlanStatusFilter)}>
      <SelectTrigger className="h-7 w-36 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">All</SelectItem>
        <SelectItem value="ACTIVE">Active</SelectItem>
        <SelectItem value="PAUSED">Paused</SelectItem>
        <SelectItem value="FAILED">Failed</SelectItem>
        <SelectItem value="CANCELLED">Cancelled</SelectItem>
        <SelectItem value="PAST_DUE">Past due</SelectItem>
        <SelectItem value="SUSPENDED">Suspended</SelectItem>
      </SelectContent>
    </Select>
  );
}

function RunStatusSelect({
  value,
  onChange,
}: {
  value: 'ALL' | RecurringBillingRunStatus;
  onChange: (value: 'ALL' | RecurringBillingRunStatus) => void;
}) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as typeof value)}>
      <SelectTrigger className="h-7 w-28 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">All</SelectItem>
        <SelectItem value="PENDING">Pending</SelectItem>
        <SelectItem value="PROCESSING">Processing</SelectItem>
        <SelectItem value="INVOICED">Invoiced</SelectItem>
        <SelectItem value="CHARGED">Charged</SelectItem>
        <SelectItem value="FAILED">Failed</SelectItem>
        <SelectItem value="SKIPPED">Skipped</SelectItem>
        <SelectItem value="DEFERRED">Deferred</SelectItem>
      </SelectContent>
    </Select>
  );
}
